import React, {useState, useEffect} from 'react';
import {ContentHeader, messageBoxSuccess} from '@deviceinsight/ng-ui-components';
import {SpinnerContainer, Button, ButtonRow} from '@deviceinsight/ng-ui-basic-components';
import {AssetGroup} from '@deviceinsight/ng-ui-api-client';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import DescriptionBox from './common/DescriptionBox';
import hasAuthority from '../../components/common/hasAuthority';
import {fetchCurrentJobSchedule, updateJobSchedule} from '../../api/updateJobsApi';
import {JobSchedule} from './types';
import './types';
import {UPDATE_JOB_SCHEDULE, ASSET_GROUP_DEFINITION} from './consts';
import DateWithInfo from './tenantscheduler/DateWithInfo';
import ExecutionDateForm from './tenantscheduler/ExecutionDateForm';
import * as translations from './tenantscheduler/translations';
import './TenantScheduler.less';
type Props = {
	assetGroup: AssetGroup & {
		definitionName: string;
	};
};

const TenantScheduler = ({assetGroup}: Props) => {
	const [job, setJob] = useState<JobSchedule | null | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [editing, setEditing] = useState<boolean>(false);
	const canReschedule = hasAuthority(UPDATE_JOB_SCHEDULE);
	const isWorkspace = assetGroup.definitionName === ASSET_GROUP_DEFINITION.WORKSPACE;
	const actualExecutionDateInfo = isWorkspace
		? translations.actualExecutionDateInfoWorkspace
		: translations.actualExecutionDateInfoInventory;
	const scheduleButton = isWorkspace ? translations.scheduleButtonWorkspace : translations.scheduleButtonInventory;

	const handleSubmit = (executionDate: string) => {
		setLoading(true);
		// @ts-expect-error This function will only be called if the job exists
		updateJobSchedule(assetGroup.id, job.id, {...job, executionDate})
			.then((updatedJob) => {
				setJob(updatedJob);
				setEditing(false);
				messageBoxSuccess(
					<Trans i18nKey="updatejobs.tenantscheduler.scheduleSuccess">
						The execution date was updated successfully
					</Trans>
				);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		setLoading(true);
		fetchCurrentJobSchedule(assetGroup.id)
			.then((pageable) => {
				setJob(pageable.content[0]);
			})
			.finally(() => setLoading(false));
	}, [assetGroup]);
	return (
		<div className="tenant-scheduler">
			<ContentHeader title={translations.headerTitle} />
			<SpinnerContainer show={loading}>
				{!job && !loading && (
					<Trans i18nKey="updatejobs.tenantscheduler.noJob">
						Currently, there is no firmware update scheduled.
					</Trans>
				)}
				{job && (
					<>
						<DescriptionBox description={job.description} showDefault={true} />
						<DateWithInfo
							data-testid="latest-execution-date-info"
							bubble={translations.latestExecutionDateInfo}
							label={translations.latestExecutionDateLabel}
							date={job.latestExecutionDate}
						/>
						{editing ? (
							<ExecutionDateForm
								loading={loading}
								onSubmit={handleSubmit}
								onCancel={() => setEditing(false)}
								isWorkspace={isWorkspace}
								initialDate={job.executionDate}
								latestExecutionDate={job.latestExecutionDate}
							/>
						) : (
							<DateWithInfo
								data-testid="actual-execution-date-info"
								bubble={actualExecutionDateInfo}
								label={translations.actualExecutionDateLabel}
								date={job.executionDate}
							/>
						)}
						{canReschedule && !editing && (
							<ButtonRow>
								<Button
									data-testid="change-execution-date-button"
									primary
									onClick={() => setEditing(true)}>
									<Trans i18nKey={scheduleButton.id}>{scheduleButton.defaultValue}</Trans>
								</Button>
							</ButtonRow>
						)}{' '}
						{isWorkspace && (
							<div className="workspace-execution-info">
								<Trans i18nKey={translations.workspaceExecutionInfo.id}>
									{translations.workspaceExecutionInfo.defaultValue}
								</Trans>
							</div>
						)}
					</>
				)}
			</SpinnerContainer>
		</div>
	);
};

export default TenantScheduler;
